<template>
  <div class="email-verification__container">
    <div class="email-verification__header">
      <label class="formester-label" :style="labelStyles">
        {{ emailVerification.title }}
      </label>

      <p class="email-verification__description">
        {{ description }}
      </p>
    </div>

    <div class="otp-container">
      <input
        v-for="(digit, index) in otpDigits"
        ref="otpInputRefs"
        :key="index"
        v-model="otpDigits[index]"
        type="number"
        class="formester-input otp-input"
        :class="{ error: hasError }"
        maxlength="1"
        min="0"
        max="9"
        placeholder="0"
        :readonly="editing"
        @input="handleInput($event, index)"
        @keydown="handleKeydown($event, index)"
        @paste="handlePaste"
      />

      <i v-if="loading" class="fa-solid fa-circle-notch fa-spin" />
    </div>

    <div v-if="hasError" class="error-message">
      Invalid confirmation code. Please try again.
    </div>

    <div v-if="emailVerification.verified" class="success-message">
      Email verified successfully!
    </div>

    <p class="email-verification__hint">
      <!-- Resend -->
      {{ emailVerification.resendDescription }}
      <span class="email-verification__hint__action-btn" @click="handleResend">
        {{ emailVerification.resendButtonText }}
      </span>

      <!-- Change Email -->
      {{ emailVerification.alternateEmailDescription }}
      <span
        class="email-verification__hint__action-btn"
        @click="handleChangeEmail"
      >
        {{ emailVerification.alternateEmailButtonText }}
      </span>
    </p>
  </div>
</template>

<script setup>
import { computed, ref, watch, onMounted, nextTick } from "vue";
import {
  sendVerificationCode,
  verifyCode,
} from "@/api/form_email_verification";
import { ElNotification } from "element-plus";

// Props
const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  styling: {
    type: Object,
    required: true,
  },
  uuid: {
    type: String,
    required: true,
  },
  editing: {
    type: Boolean,
    default: false,
  },
});

// Emits
const emit = defineEmits(["verification-complete"]);

// State
const otpDigits = ref(Array(6).fill(""));
const hasError = ref(false);
const otpInputRefs = ref([]);
const loading = ref(false);

// Mounted
onMounted(() => {
  otpInputRefs.value[0]?.setCustomValidity(
    "Please confirm your email address by entering the confirmation code.",
  );
});

// Computed
const emailVerification = computed(() => props.item.emailConfirmationData);

const labelStyles = computed(() => {
  return {
    "margin-bottom": `${props.styling.labelBottomMargin}px`,
    color: props.styling.fontColor,
    "font-family": props.styling.fontFamily,
    "font-size": `${props.styling.fontSize}px`,
    "font-weight": props.styling.labelFontWeight,
    "font-style": props.styling.italicFont ? "italic" : "normal",
  };
});

const description = computed(() =>
  emailVerification.value.description.replace(
    "{{email}}",
    props.item.value || "your email",
  ),
);

// Methods
function handleInput(event, index) {
  const value = event.target.value;

  // Allow only single digit
  otpDigits.value[index] = value.slice(-1);

  // Move to next after value is entered
  if (value && index < 5) {
    otpInputRefs.value[index + 1].focus();
  }

  if (value && index === 5) {
    verifyOTP();
  }
}

function handleKeydown(event, index) {
  if (props.editing) return;

  // Handle backspace
  if (event.key === "Backspace" && !otpDigits.value[index] && index > 0) {
    otpDigits.value[index - 1] = "";
    otpInputRefs.value[index - 1].focus();
  } else if (event.key === "Enter") {
    event.preventDefault();
    verifyOTP();
  }
}

function handlePaste(event) {
  if (props.editing) return;

  event.preventDefault();
  const pastedData = event.clipboardData.getData("text");
  const numbers = pastedData.replace(/\D/g, "").slice(0, 6);

  numbers.split("").forEach((num, index) => {
    if (index < 6) {
      otpDigits.value[index] = num;
      otpInputRefs.value[index + 1]?.focus();
    }
  });

  if (numbers.length >= 6) {
    verifyOTP();
  }
}

async function handleResend() {
  if (props.editing) return;

  loading.value = true;
  try {
    const mailAccountId = emailVerification.value.mailIntegrationId;
    const { data } = await sendVerificationCode(
      props.uuid,
      props.item.value,
      mailAccountId,
    );
    loading.value = false;

    ElNotification({
      message: data.message,
      type: "success",
      position: "bottom-right",
    });
  } catch (error) {
    loading.value = false;
    const errorMessage =
      error.response?.data?.message ||
      "Unable to resend confirmation code. Please try again.";

    ElNotification({
      message: errorMessage,
      type: "error",
      position: "bottom-right",
    });
  }
}

function handleChangeEmail() {
  if (props.editing) return;

  emailVerification.value.verifying = false;
}

async function verifyOTP() {
  const otp = otpDigits.value.join("");
  loading.value = true;

  try {
    const { data } = await verifyCode(props.uuid, props.item.value, otp);

    loading.value = false;
    hasError.value = false;
    emailVerification.value.verifying = false;
    emailVerification.value.verified = true;
    emailVerification.value.code = otp;

    nextTick(() => {
      emit("verification-complete");
    });

    ElNotification({
      message: data.message,
      type: "success",
      position: "bottom-right",
    });
  } catch (error) {
    console.error(error);
    loading.value = false;
    hasError.value = true;
    const errorMessage =
      error.response?.data?.message ||
      "Unable to confirm code. Please try again.";

    ElNotification({
      message: errorMessage,
      type: "error",
      position: "bottom-right",
    });
  }
}

watch(
  otpDigits,
  () => {
    if (hasError.value) {
      hasError.value = false;
    }
  },
  { deep: true },
);
</script>

<style scoped>
.email-verification__container {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: var(--spacing-2);
}

.email-verification__header {
  margin-bottom: var(--spacing-2);
}

.email-verification__header label {
  display: block;
  margin-bottom: 8px;
  word-wrap: break-word;
  line-height: 150%;
}

.email-verification__description {
  color: var(--description-font-color);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-sm);
  margin-bottom: 0;
}

.otp-container {
  display: flex;
  gap: var(--spacing-2);
}

.otp-input {
  width: 40px;
  height: 50px;
  text-align: center;
  transition: all 0.2s ease;
  appearance: none;
  -moz-appearance: textfield;
}
.otp-input::placeholder {
  color: var(--placeholder-text-color);
}
.otp-input::-webkit-outer-spin-button,
.otp-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.otp-input:focus {
  border-color: transparent;
}

.otp-input:read-only:focus {
  border-color: var(--border-color);
}

.otp-input.error {
  border-color: #dc2626;
  animation: shake 0.3s;
}

.error-message {
  color: #dc2626;
  font-size: var(--font-size-sm);
}

.success-message {
  color: #16a34a;
  font-size: var(--font-size-sm);
}

@keyframes shake {
  0%,
  100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(-5px);
  }
  75% {
    transform: translateX(5px);
  }
}

.email-verification__hint {
  font-size: var(--font-size-sm);
  color: var(--description-font-color);
  margin: 0;
  margin-top: var(--spacing-2);
}

.email-verification__hint__action-btn {
  color: var(--primary-color);
  cursor: pointer;
}

.otp-container i {
  font-size: 20px;
  color: var(--primary-color);
  display: flex;
  align-items: center;
}
</style>
